import type { CSSProperties } from 'react';

/**
 * 랜덤박스 제목 색 - lottery_product / fanza_product 의 order_color.
 *
 * 원본 lottery_load_more_data_kr.php 는 이 값을 검은 배경 표에 그대로
 * style="color: …" 로 찍었다. 그래서 골라 둔 값 대부분이 검은 바탕용 밝은
 * 파스텔(#ffffff·#fed7f2·#ffd500 …)이고, 몇 개는 어두운 색(#0040ff·#8a1515)
 * 이다 (2026-11-03 실데이터 63건 기준, 전부 #rrggbb).
 *
 * 그대로 쓰면 라이트 테마의 흰 표에서 #ffffff 는 아예 안 보인다. 그래서 테마별
 * 표 배경과의 명도 대비가 WCAG AA(4.5:1) 이상이면 설정값을 그대로 쓰고,
 * 모자라면 색상(hue)은 두고 꼭 필요한 만큼만 라이트에선 어둡게, 다크에선 밝게
 * 옮긴다. 다크 테마는 원본의 검은 배경과 비슷해서 대부분 설정값 그대로 나온다.
 *
 * DB 자유 입력값을 style 에 넣는 것이라 #rgb / #rrggbb 만 받고 나머지는
 * 버린다(그 행은 기본 글자색).
 */

type Rgb = [number, number, number];

/** styles.css 의 --bg-raised (표를 감싼 .table-wrap 배경). 그쪽을 바꾸면 같이 바꿀 것. */
const LIGHT_BG: Rgb = [0xff, 0xff, 0xff];
const DARK_BG: Rgb = [0x16, 0x1b, 0x22];
const MIN_RATIO = 4.5;
const BLACK: Rgb = [0, 0, 0];
const WHITE: Rgb = [255, 255, 255];

export function parseHexColor(value: string | null | undefined): Rgb | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value?.trim() ?? '');
  if (!m) return null;
  const hex = m[1]!.length === 3 ? [...m[1]!].map((c) => c + c).join('') : m[1]!;
  return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16)) as Rgb;
}

function luminance(rgb: Rgb): number {
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 명도 대비 (1 ~ 21). */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * color 를 toward(검정/흰색) 쪽으로 t 만큼 섞는다. sRGB 에서 검정과 섞으면 세
 * 채널 비율이 그대로라 색상이 유지된다. 반올림도 toward 쪽으로 해서, 섞은 뒤
 * 대비가 반올림 때문에 기준 밑으로 떨어지지 않게 한다.
 */
function mix(color: Rgb, toward: Rgb, t: number): Rgb {
  const round = toward === BLACK ? Math.floor : Math.ceil;
  return color.map((c, i) => round(c + (toward[i]! - c) * t)) as Rgb;
}

const toHex = (rgb: Rgb) => `#${rgb.map((c) => c.toString(16).padStart(2, '0')).join('')}`;

/** background 위에서 minRatio 이상 읽히는, color 에 가장 가까운 색 (#rrggbb). */
export function readableOn(color: Rgb, background: Rgb, minRatio = MIN_RATIO): string {
  if (contrastRatio(color, background) >= minRatio) return toHex(color);
  const toward = luminance(background) > 0.5 ? BLACK : WHITE;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 20; i++) {
    const mid = (lo + hi) / 2;
    if (contrastRatio(mix(color, toward, mid), background) >= minRatio) hi = mid;
    else lo = mid;
  }
  return toHex(mix(color, toward, hi));
}

/**
 * 제목 span 에 줄 style. 테마별 색을 CSS 변수 두 개로 넘기고, 고르는 건
 * styles.css 의 .title-color 가 light-dark() 로 한다 - JS 가 지금 테마를 몰라도
 * 되고, 테마를 바꾸면 다시 그리지 않아도 바로 따라간다.
 * 쓸 수 없는 값이면 undefined (기본 글자색).
 */
export function titleColorStyle(value: string | null | undefined): CSSProperties | undefined {
  const rgb = parseHexColor(value);
  if (!rgb) return undefined;
  return {
    '--title-color-light': readableOn(rgb, LIGHT_BG),
    '--title-color-dark': readableOn(rgb, DARK_BG),
  } as CSSProperties;
}
